import React, { useState } from "react";
import { Filter, Calendar, X, ArrowLeft, Check, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast"; // Assuming toast is available globally/imported

export default function TopHeader({ loadSettlement }) {
  const navigate = useNavigate();

  const [showFilter, setShowFilter] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [activeFilter, setActiveFilter] = useState("Last Week"); // Set initial active filter

  // --- Configuration ---
  const filters = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "Last 3 Months",
    "Last Year",
    "Custom",
  ];

  // --- Handlers ---
  const handleFilterSelect = (item) => {
    let start, end;
    
    // 1. Set the active filter label
    setActiveFilter(item);

    switch (item) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "day").startOf("day");
        end = moment().subtract(1, "day").endOf("day");
        break;
      case "This Week":
        start = moment().startOf("week");
        end = moment().endOf("week");
        break;
      case "Last Week":
        start = moment().subtract(1, "week").startOf("week");
        end = moment().subtract(1, "week").endOf("week");
        break;
      case "This Month":
        start = moment().startOf("month");
        end = moment().endOf("month");
        break;
      case "Last Month":
        start = moment().subtract(1, "month").startOf("month");
        end = moment().subtract(1, "month").endOf("month");
        break;
      case "Last 3 Months":
        // Start 3 months ago, end is today (EOD) for complete data
        start = moment().subtract(3, "months").startOf("month");
        end = moment().endOf("day"); 
        break;
      case "Last Year":
        // Start 1 year ago, end is today (EOD) for complete data
        start = moment().subtract(1, "year").startOf("year");
        end = moment().endOf("day"); 
        break;
      case "Custom":
        setShowCustom(true);
        return;
      default:
        return;
    }

    // 2. Call loadSettlement with formatted dates
    loadSettlement(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    setShowFilter(false);
  };

  const handleCustomSubmit = () => {
    if (!customRange.start || !customRange.end) {
        toast.error("Please select both start and end dates.");
        return;
    }
    
    // Update active filter display
    setActiveFilter(`Custom: ${customRange.start} to ${customRange.end}`);

    loadSettlement(customRange.start, customRange.end);
    setShowCustom(false);
    setShowFilter(false);
  };

  // --- Rendering ---
  return (
    <>
      {/* 🔝 Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b-2 border-[var(--hover)] bg-[var(--background)] shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg border-2 border-[var(--hover)] bg-[var(--hover)]/30 transition hover:bg-[var(--hover)]"
        >
          <ArrowLeft className="w-5 h-5 text-[var(--subtext)]" />
        </button>

        <div className="text-[var(--text)] font-bold text-base select-none flex flex-col items-center">
            Settlement
            <span className="text-[10px] text-[var(--subtext)] font-medium opacity-80 mt-[2px]">
                {activeFilter.includes('Custom') ? 'Custom Range' : activeFilter}
            </span>
        </div>

        <button
          onClick={() => setShowFilter(true)}
          className="p-2 rounded-lg border-2 border-[var(--hover)] bg-[var(--hover)]/30 transition hover:bg-[var(--hover)] relative"
        >
          <Filter className="w-5 h-5 text-[var(--subtext)]" />
          {/* Active indicator (optional, but good for UX if a filter is applied) */}
          {activeFilter !== "Last Week" && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-[var(--primary)] rounded-full animate-pulse"></span>
          )}
        </button>
      </div>

     {/* 🧭 Filter Modal (Z-INDEX: 50) */}
      <AnimatePresence>
        {showFilter && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-45"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFilter(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-t-[var(--hover)] bg-[var(--background)] rounded-t-3xl shadow-xl p-6 max-w-md mx-auto"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[var(--text)] flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[var(--primary)]" /> Select Date Range
                </h2>
                <button
                  onClick={() => setShowFilter(false)}
                  className="p-2 rounded-full hover:bg-[var(--hover)]"
                >
                  <X className="w-5 h-5 text-[var(--subtext)]" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {filters.map((item) => (
                  <button
                    key={item}
                    onClick={() => handleFilterSelect(item)}
                    className={`
                        px-3 py-3 text-sm rounded-lg border-2 transition shadow-sm font-medium flex justify-center items-center gap-2
                        ${item === activeFilter ? 
                            "border-[var(--primary)] bg-[var(--primary)] text-white" : 
                            "border-[var(--hover)] bg-[var(--background)] text-[var(--text)] hover:border-[var(--primary)]"
                        }`}
                  >
                    {item}
                    {item === activeFilter && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 📅 Custom Range Modal (Z-INDEX: 55) */}
      <AnimatePresence>
        {showCustom && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur z-54"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCustom(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-55 bg-[var(--background)] rounded-t-3xl shadow-lg p-6 max-w-md mx-auto border-t-2 border-t-[var(--primary)]"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[var(--text)] flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[var(--primary)]" /> Custom Range
                </h2>
                <button
                  onClick={() => setShowCustom(false)}
                  className="p-2 rounded-full hover:bg-[var(--hover)]"
                >
                  <X className="w-5 h-5 text-[var(--subtext)]" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[var(--subtext)] mb-1 block font-medium">
                    Start Date
                  </label>
                  <input
                    type="date"
                    max={moment().format("YYYY-MM-DD")} // Max date is today
                    value={customRange.start}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, start: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 rounded-lg bg-transparent text-[var(--text)] border-[var(--hover)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none transition"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--subtext)] mb-1 block font-medium">
                    End Date
                  </label>
                  <input
                    type="date"
                    max={moment().format("YYYY-MM-DD")} // Max date is today
                    value={customRange.end}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, end: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 rounded-lg bg-transparent text-[var(--text)] border-[var(--hover)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none transition"
                  />
                </div>

                <button
                  onClick={handleCustomSubmit}
                  disabled={!customRange.start || !customRange.end}
                  className="w-full bg-[var(--primary)] text-white py-3 rounded-lg font-bold text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Filter
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}